import type { ModuleKey, Role } from '@radiology/ui';

/**
 * Keycloak role vocabulary -> canonical role vocabulary.
 *
 * Roles live as per-client roles in the NGHA realm, and the four clients spell
 * them inconsistently: `Employee` in cert-mng but `employee` in
 * purchase-tracking, and `chief` for what the UI calls a chief approver.
 * Verified against the realm dump on 2026-07-30.
 *
 * Normalising here — at the single point where Keycloak data enters the app —
 * means no component ever branches on a Keycloak-specific spelling, and the
 * realm needs no migration.
 */
export const KEYCLOAK_CLIENT_IDS: Record<ModuleKey, string> = {
  certifications: 'cert-mng',
  purchasing: 'purchase-tracking',
  attendance: 'qr-attendance',
  devices: 'medical-devices',
};

const ROLE_ALIASES: Record<string, Role> = {
  admin: 'admin',
  'operation-manager': 'operation-manager',
  operationmanager: 'operation-manager',
  'operation manager': 'operation-manager',
  manager: 'manager',
  supervisor: 'supervisor',
  approver: 'approver',
  chief: 'chief-approver',
  'chief-approver': 'chief-approver',
  chiefapprover: 'chief-approver',
  employee: 'employee',
  secretary: 'secretary',
  management: 'management',
};

/** Returns null for a role we do not recognise, so unknown roles grant nothing. */
export function normalizeRole(raw: string): Role | null {
  return ROLE_ALIASES[raw.trim().toLowerCase()] ?? null;
}

export function normalizeRoles(raw: string[]): Role[] {
  const seen = new Set<Role>();
  for (const value of raw) {
    const role = normalizeRole(value);
    if (role) seen.add(role);
  }
  return [...seen];
}

/**
 * Which role's screens to show when a user holds several in one module.
 * Higher index wins. Broadest scope first so an admin does not land on the
 * employee dashboard.
 */
const ROLE_PRECEDENCE: Role[] = [
  'employee',
  'secretary',
  'approver',
  'supervisor',
  'chief-approver',
  'management',
  // The restricted Manager sits BELOW Operation Manager: someone holding both
  // should land on the unrestricted screens, not the narrow ones.
  'manager',
  'operation-manager',
  'admin',
];

export function primaryRole(roles: Role[]): Role | undefined {
  return [...roles].sort((a, b) => ROLE_PRECEDENCE.indexOf(a) - ROLE_PRECEDENCE.indexOf(b)).at(-1);
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  'operation-manager': 'Operation manager',
  manager: 'Manager',
  supervisor: 'Supervisor',
  approver: 'Approver',
  'chief-approver': 'Chief approver',
  employee: 'Employee',
  secretary: 'Secretary',
  management: 'Management',
};
